import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { ref, get, push, query, orderByChild, equalTo } from "firebase/database";
import { useAuth } from "../context/AuthContext";
import { Bell, Plus, X, AlertCircle, Pin, ArrowLeft } from "lucide-react";

const NOTICE_CATEGORIES = [
  { label: "Exam Schedule", color: "bg-red-100 text-red-700" },
  { label: "Fee Deadline", color: "bg-orange-100 text-orange-700" },
  { label: "Event", color: "bg-purple-100 text-purple-700" },
  { label: "Holiday", color: "bg-green-100 text-green-700" },
  { label: "Placement", color: "bg-blue-100 text-blue-700" },
  { label: "General", color: "bg-gray-100 text-gray-700" },
  { label: "Emergency", color: "bg-red-200 text-red-800" },
];

function getCategoryStyle(cat) {
  return NOTICE_CATEGORIES.find(c => c.label === cat)?.color || "bg-gray-100 text-gray-700";
}

function NoticeCard({ notice }) {
  return (
    <div className={`bg-white rounded-xl shadow-sm p-4 border ${notice.pinned ? "border-yellow-300" : "border-gray-100"}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            {notice.pinned && <Pin size={12} className="text-yellow-500 fill-yellow-500" />}
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${getCategoryStyle(notice.category)}`}>
              {notice.category}
            </span>
            <span className="text-xs text-gray-400">{notice.college}</span>
          </div>
          <p className="font-bold text-gray-800 text-sm">{notice.title}</p>
          <p className="text-sm text-gray-600 mt-1 leading-relaxed">{notice.content}</p>
        </div>
      </div>
      <div className="flex items-center justify-between mt-3 pt-2 border-t border-gray-50">
        <p className="text-xs text-gray-400">Posted by {notice.postedByName}</p>
        <p className="text-xs text-gray-400">
          {notice.createdAt
            ? new Date(notice.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "short" })
            : "Recently"}
        </p>
      </div>
    </div>
  );
}

export default function NoticeBoard() {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");
  const [form, setForm] = useState({ title: "", content: "", category: "General", pinned: false });
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => { fetchNotices(); }, []);

  async function fetchNotices() {
    setLoading(true);
    try {
      const snap = await get(ref(db, "notices"));
      if (snap.exists()) {
        const all = Object.entries(snap.val()).map(([id, data]) => ({ id, ...data }));
        setNotices(all.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
      } else {
        setNotices([]);
      }
    } catch (e) {  }
    setLoading(false);
  }

  function handleChange(e) {
    const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm({ ...form, [e.target.name]: val });
  }

  async function handlePost(e) {
    e.preventDefault();
    setError("");
    if (!form.title.trim() || !form.content.trim()) return setError("Please fill in title and content.");
    setPosting(true);
    try {
      await push(ref(db, "notices_pending"), {
        title: form.title.trim(),
        content: form.content.trim(),
        category: form.category,
        pinned: false,
        postedBy: currentUser.uid,
        postedByName: userProfile?.name || "Student",
        college: userProfile?.college || "All Colleges",
        createdAt: new Date().toISOString(),
        status: "pending",
      });
      setForm({ title: "", content: "", category: "General", pinned: false });
      setShowForm(false);
      setSubmitted(true);
      setTimeout(() => setSubmitted(false), 4000);
    } catch (err) { setError("Failed to post. Try again."); }
    setPosting(false);
  }

  const filtered = activeCategory === "All" ? notices : notices.filter(n => n.category === activeCategory);
  const pinned = filtered.filter(n => n.pinned);
  const regular = filtered.filter(n => !n.pinned);

  return (
    <div className="pb-24 bg-gray-50 min-h-screen">
      <div className="bg-gradient-to-r from-indigo-600 to-blue-700 px-4 pt-4 pb-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="p-1.5 -ml-1.5 rounded-full hover:bg-white/20"><ArrowLeft size={20} /></button>
            <div>
              <h1 className="text-xl font-bold">Notice Board</h1>
              <p className="text-indigo-100 text-xs mt-0.5">Campus announcements & updates</p>
            </div>
          </div>
          <button onClick={() => setShowForm(true)}
            className="bg-white text-indigo-700 px-3 py-1.5 rounded-xl font-semibold text-sm flex items-center gap-1">
            <Plus size={16} /> Post Notice
          </button>
        </div>
      </div>

      {/* Category Filter */}
      <div className="px-4 -mt-3 mb-4">
        <div className="bg-white rounded-xl shadow-sm p-3">
          <div className="flex gap-2 overflow-x-auto pb-1">
            {["All", ...NOTICE_CATEGORIES.map(c => c.label)].map(cat => (
              <button key={cat} onClick={() => setActiveCategory(cat)}
                className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-medium flex-shrink-0 ${activeCategory === cat ? "bg-indigo-600 text-white" : "bg-gray-100 text-gray-600"}`}>
                {cat}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="px-4 space-y-3">
        {loading ? (
          [1,2,3].map(i => <div key={i} className="h-24 bg-white rounded-xl animate-pulse" />)
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl">
            <Bell size={48} className="mx-auto mb-3 text-gray-200" />
            <p className="text-gray-500 font-medium">No notices posted yet</p>
            <button onClick={() => setShowForm(true)} className="mt-4 bg-indigo-600 text-white px-6 py-2.5 rounded-xl font-semibold text-sm">
              Post First Notice
            </button>
          </div>
        ) : (
          <>
            {pinned.length > 0 && (
              <div>
                <p className="text-xs font-bold text-yellow-600 mb-2 flex items-center gap-1">
                  <Pin size={12} className="fill-yellow-500" /> PINNED
                </p>
                <div className="space-y-3">{pinned.map(n => <NoticeCard key={n.id} notice={n} />)}</div>
              </div>
            )}
            {regular.length > 0 && (
              <div className="space-y-3">{regular.map(n => <NoticeCard key={n.id} notice={n} />)}</div>
            )}
          </>
        )}
      </div>

      {submitted && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 bg-green-600 text-white px-5 py-3 rounded-xl shadow-lg text-sm font-semibold">
          Notice submitted! Pending admin approval.
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-end">
          <div className="bg-white rounded-t-2xl w-full max-h-[85vh] overflow-y-auto">
            <div className="sticky top-0 bg-white px-4 py-4 border-b flex items-center justify-between">
              <h3 className="font-bold text-gray-800 text-lg">Post a Notice</h3>
              <button onClick={() => { setShowForm(false); setError(""); }}><X size={24} /></button>
            </div>
            <form onSubmit={handlePost} className="p-4 space-y-3 pb-24">
              {error && <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm"><AlertCircle size={16} />{error}</div>}

              <select name="category" value={form.category} onChange={handleChange}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-indigo-400 text-gray-700">
                {NOTICE_CATEGORIES.map(c => <option key={c.label} value={c.label}>{c.label}</option>)}
              </select>

              <input name="title" value={form.title} onChange={handleChange} required
                placeholder="Notice title"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-indigo-400" />

              <textarea name="content" value={form.content} onChange={handleChange} required rows={4}
                placeholder="Notice details..."
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-indigo-400 resize-none" />

              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" name="pinned" checked={form.pinned} onChange={handleChange}
                  className="w-4 h-4 rounded accent-indigo-600" />
                <span className="text-sm text-gray-700">Pin this notice to top</span>
              </label>

              <button type="submit" disabled={posting}
                className="w-full bg-indigo-600 text-white py-3.5 rounded-xl font-semibold hover:bg-indigo-700 disabled:opacity-50">
                {posting ? "Posting..." : "Post Notice"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
